import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWithdrawalsMutations } from '../../hooks/useWithdrawalsMutations';
import type { Withdrawal } from '../../models';

export interface MarkReturnedWithdrawalModalRef {
  open: (withdrawal: Withdrawal) => void;
  close: () => void;
}

interface MarkReturnedWithdrawalModalProps {
  onSuccess?: () => void;
}

export const MarkReturnedWithdrawalModal = forwardRef<MarkReturnedWithdrawalModalRef, MarkReturnedWithdrawalModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('withdrawalsModule');
    const { toast } = useToast();
    const currency = useCurrency();
    const modalRef = useRef<ModalCenterRef>(null);
    const { markReturned } = useWithdrawalsMutations();

    const [withdrawal, setWithdrawal] = useState<Withdrawal | null>(null);

    useImperativeHandle(ref, () => ({
      open: (w) => {
        setWithdrawal(w);
        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const handleConfirm = async () => {
      if (!withdrawal) return;
      try {
        await markReturned.mutateAsync(withdrawal.id);
        toast({
          title: t('modals.markReturned.success.title'),
          description: t('modals.markReturned.success.description'),
        });
        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.markReturnedFailed'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isLoading = markReturned.isPending;

    return (
      <ModalCenter ref={modalRef} title={t('modals.markReturned.title')} width="default">
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t('modals.markReturned.description')}</p>

          {withdrawal && (
            <div className="bg-muted rounded-lg p-3 space-y-1">
              <p className="text-sm">
                <span className="font-medium">ID:</span> #{withdrawal.id}
              </p>
              <p className="text-sm">
                <span className="font-medium">Importe aprobado:</span> {withdrawal.approved_amount ?? '—'} {currency}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="secondary" onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? t('buttons.marking') : t('actions.markReturned')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

MarkReturnedWithdrawalModal.displayName = 'MarkReturnedWithdrawalModal';
